/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import {
  LiveKitRoom,
  useConnectionState,
  useLocalParticipant,
  useTracks,
  AudioTrack,
  useVoiceAssistant,
} from "@livekit/components-react";
import { ConnectionState, LocalParticipant, Track } from "livekit-client";
import { X } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { FaMicrophone, FaSquareFull } from "react-icons/fa";
import { IoChatbubble } from "react-icons/io5";
import { useCreateChatContextMutation } from "../../apis/chat.api";
import { Button } from "../../components/ui/button";
import { useVoiceChat } from "../../hooks/use-voice-chat";
import { WidgetScene } from "../types/widget-scenes";
import AgentAvatar from "./agent-avatar";
import { iChoiceProps } from "./choice";

const ChoiceCall: FC<iChoiceProps> = ({ setWidgetStage, agentId, config }) => {
  const [createChatContext] = useCreateChatContextMutation();
  const [chatId, setChatId] = useState<string>("");
  const [callDuration, setCallDuration] = useState("00:00");
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolumeState] = useState(1.0);
  const chatContextCreated = useRef(false);
  const { voiceToken, initializeVoiceChat } = useVoiceChat({
    chatId,
  });

  useEffect(() => {
    console.debug({
      agentId,
      chatId,
      chatContextCreated: chatContextCreated.current,
    });
    if (agentId && !chatId && !chatContextCreated.current) {
      chatContextCreated.current = true;
      const storedChatId = localStorage.getItem(`chatId_${agentId}`);

      if (storedChatId) {
        setChatId(storedChatId);
        return;
      }

      createChatContext({ agentId })
        .unwrap()
        .then((response) => {
          console.debug({ response });
          setChatId(response.chatId);
          localStorage.setItem(`chatId_${agentId}`, response.chatId);
        })
        .catch((error) => {
          console.error("Failed to create chat context:", error);
        });
    }
  }, [agentId, chatId, createChatContext]);

  useEffect(() => {
    if (chatId) {
      initializeVoiceChat();
    }
  }, [chatId, initializeVoiceChat]);

  const onClose = () => {
    setWidgetStage(WidgetScene.CHOICE);
  };

  const onChatSelection = () => {
    setWidgetStage(WidgetScene.CHAT);
  };

  const onEndCall = () => {
    setWidgetStage(WidgetScene.CHOICE);
  };

  const handleMicrophoneToggle = async () => {
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolumeState(newVolume);
  };

  useEffect(() => {
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const minutes = Math.floor(elapsed / 60000);
      const seconds = Math.floor((elapsed % 60000) / 1000);
      setCallDuration(
        `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
          2,
          "0"
        )}`
      );
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  if (!config) return null;

  return (
    <LiveKitRoom
      serverUrl={
        process.env.NEXT_PUBLIC_LIVEKIT_URL ||
        "wss://outsella-demo-g3d9d836.livekit.cloud"
      }
      token={voiceToken || ""}
      connect={!!voiceToken}
      audio={true}
      options={{
        publishDefaults: { stopMicTrackOnMute: true },
      }}
      onError={(e) => {
        console.error("LiveKit room error:", e);
      }}
      onDisconnected={() => {
        console.log("LiveKit room disconnected");
      }}
      onConnected={() => {
        console.log("LiveKit room connected");
      }}
    >
      <LiveKitRoomContent
        isMuted={isMuted}
        volume={volume}
        callDuration={callDuration}
        config={config}
        onClose={onClose}
        onChatSelection={onChatSelection}
        onEndCall={onEndCall}
        handleMicrophoneToggle={handleMicrophoneToggle}
        handleVolumeChange={handleVolumeChange}
      />
    </LiveKitRoom>
  );
};

const LiveKitRoomContent: FC<{
  isMuted: boolean;
  volume: number;
  callDuration: string;
  config: iChoiceProps["config"];
  onClose: () => void;
  onChatSelection: () => void;
  onEndCall: () => void;
  handleMicrophoneToggle: () => void;
  handleVolumeChange: (volume: number) => void;
}> = ({
  isMuted,
  volume,
  callDuration,
  config,
  onClose,
  onChatSelection,
  onEndCall,
  handleMicrophoneToggle,
}) => {
  const roomState = useConnectionState();
  const { localParticipant } = useLocalParticipant();
  const { audioTrack } = useVoiceAssistant();
  const tracks = useTracks();
  const hasStartedMic = useRef(false);
  const micEnabledRef = useRef(false);
  const connectionAttempted = useRef(false);

  const localTracks = tracks.filter(
    ({ participant }) => participant instanceof LocalParticipant
  );
  const localMicTrack = localTracks.find(
    ({ source }) => source === Track.Source.Microphone
  );

  useEffect(() => {
    if (localParticipant && roomState === ConnectionState.Connected) {
      console.log(`Setting microphone ${isMuted ? "muted" : "unmuted"}`);
      localParticipant.setMicrophoneEnabled(!isMuted);
      micEnabledRef.current = !isMuted;
    }
  }, [isMuted, localParticipant, roomState]);

  useEffect(() => {
    if (
      roomState === ConnectionState.Connected &&
      localParticipant &&
      !hasStartedMic.current
    ) {
      console.log("Room connected, enabling microphone");
      hasStartedMic.current = true;

      const timer = setTimeout(() => {
        if (!connectionAttempted.current) {
          console.log("Room is fully connected, enabling microphone");
          try {
            localParticipant.setMicrophoneEnabled(true);
            micEnabledRef.current = true;
            connectionAttempted.current = true;
          } catch (error) {
            console.error("Error enabling microphone:", error);
          }
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [roomState, localParticipant]);

  useEffect(() => {
    if (localMicTrack?.track) {
      localMicTrack.track.setVolume(volume);
    }
  }, [volume, localMicTrack]);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.srcObject = null;
      }
    };
  }, []);

  useEffect(() => {
    console.log("Connection state changed:", roomState);
  }, [roomState]);

  return (
    <div className="bg-white shadow-widget text-center w-[360px] rounded-[31px] flex flex-col items-center gap-6">
      <div className="p-1 w-full">
        <div className="w-full flex gap-10 flex-col items-center text-white rounded-[31px] p-4 py-3 pb-5 bg-neutral-800">
          <div className="flex w-full justify-between items-center">
            <p className="font-medium">
              Call with {config.ai_agent?.agent_name}
            </p>
            <Button
              onClick={onClose}
              className="rounded-full bg-[rgba(255,255,255,0.14)] border-none size-[27px]"
              variant="outline"
              size="icon"
            >
              <X />
            </Button>
            {audioTrack && <AudioTrack trackRef={audioTrack} />}
          </div>
          <AgentAvatar config={config} className="size-[84px]" />
          <div>
            <h5 className="text-[15px]">
              {config?.ai_agent?.agent_name} {config?.ai_agent?.role?.name}
            </h5>
            <p className="opacity-50 text-[14px] mt-1">
              {roomState === ConnectionState.Connected
                ? "Connected"
                : "Connecting..."}{" "}
              • {callDuration}
            </p>
          </div>
        </div>
      </div>

      <div className="py-3 w-full bg-gray-100 flex items-center justify-around gap-4 rounded-b-[31px]">
        <Button
          className="rounded-full"
          variant="ghost"
          onClick={handleMicrophoneToggle}
          disabled={roomState !== ConnectionState.Connected}
        >
          <FaMicrophone className={isMuted ? "opacity-50" : ""} />
          <span>Mute</span>
        </Button>

        <Button onClick={onEndCall} className="rounded-full" variant="ghost">
          <FaSquareFull className="size-[12px] text-red-500" />
          <span>End call</span>
        </Button>

        <Button
          className="rounded-full"
          variant="ghost"
          onClick={onChatSelection}
        >
          <IoChatbubble className="opacity-50" />
          <span>Chat</span>
        </Button>
      </div>
    </div>
  );
};

export default ChoiceCall;
